import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAppStore } from '@/store';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { User, Settings, CreditCard, Home, Bot, Upload, Trash2 } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useSubscription } from '@/hooks/useSubscription'; // Import useSubscription
import { useAuth } from '@/contexts/AuthContext';
import UserBadge from '@/components/users/UserBadge';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Profile as ProfileType } from '@/types/supabase';
import { fetchOllamaModels } from '@/lib/ollama';
import { AIModel, ModelProvider, UserProfile } from '@/types';
import ModelSelector from '@/components/chat/ModelSelector';
import LoadingScreen from '@/components/ui/loading-screen'; // Import LoadingScreen
import PasswordSettings from '@/components/settings/PasswordSettings';

const FOUNDERS_END_DATE = new Date('2025-09-01T00:00:00Z');

const Profile = () => {
  const { user, setUser } = useAppStore();
  const { profile, loading: profileLoading, updateProfile, uploadAvatar, deleteAvatar } = useProfile();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user: authUser } = useAuth();
  const { models, fetchModels } = useSupabaseStore();
  const { currentTier, subscriptionLoading, fetchAndSetCurrentTier } = useSubscription(); // Destructure fetchAndSetCurrentTier

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [ollamaModels, setOllamaModels] = useState<Array<{ name: string; source: 'local' | 'remote' }>>([]);
  const [isLoadingOllama, setIsLoadingOllama] = useState(false);

  useEffect(() => {
    fetchModels();
    setIsLoadingOllama(true);
    fetchOllamaModels()
      .then(setOllamaModels)
      .catch(() => setOllamaModels([]))
      .finally(() => setIsLoadingOllama(false));
  }, [fetchModels]);

  const availableModels = useMemo(() => {
    const ollamaVirtualModels: AIModel[] = ollamaModels.map(({ name, source }) => ({
      id: `ollama-${source}-${name.replace(/[:]/g, '-').replace(/\s/g, '')}`,
      name: name,
      provider: 'ollama' as ModelProvider,
      description: `Ollama (${source === 'local' ? 'Local' : 'Remote'}): ${name}`,
      capabilities: ['text', 'code'],
      tier: 'free',
      apiName: name,
      is_active: true,
      source,
      created_at: new Date().toISOString(),
      context_length: null,
      logo_url: null,
    }));

    const combined = [...models, ...ollamaVirtualModels];
    const uniqueModels: AIModel[] = [];
    const seenApiNames = new Set<string>();

    combined.forEach(model => {
      if (!seenApiNames.has(model.apiName)) {
        uniqueModels.push(model);
        seenApiNames.add(model.apiName);
      }
    });
    
    return uniqueModels.sort((a, b) => a.name.localeCompare(b.name));
  }, [models, ollamaModels]);

  useEffect(() => {
    if (availableModels.length === 0) return;

    let modelToSet: AIModel | undefined;
    const userProfile = profile as UserProfile | null;

    if (userProfile?.defaultModelApiName) { // Now correctly using defaultModelApiName
      // Find the model by its apiName
      modelToSet = availableModels.find(m => m.apiName === userProfile.defaultModelApiName);
    }

    if (modelToSet) {
      setDefaultModel(modelToSet.id); // Set the ID for the ModelSelector component
    } else if (availableModels.length > 0) {
      setDefaultModel(availableModels[0].id);
    }
  }, [profile, availableModels]);

  useEffect(() => {
    fetchAndSetCurrentTier();
  }, [user, fetchAndSetCurrentTier]);

  useEffect(() => {
    if (profile) {
      // Always initialize with the profile's name (which comes from full_name in DB or email fallback)
      setName(profile.name || ''); 
      setEmail(authUser?.email || profile.email || '');
    } else if (!profileLoading && authUser) {
      setName(authUser.email?.split('@')[0] || '');
      setEmail(authUser.email || '');
    } else if (!profileLoading && !authUser) {
      setName('');
      setEmail('');
    }
  }, [profile, authUser, profileLoading]);

  const handleSaveAll = async () => {
    if (!user || !profile) {
      toast({
        title: 'Error',
        description: 'User profile not fully loaded. Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Find the selected model by its ID to get its apiName for saving
    const selectedModel = availableModels.find(m => m.id === defaultModel);

    try {
      const updates: Partial<ProfileType> = {
        full_name: name,
        default_model: selectedModel?.apiName,
      };

      const { success } = await updateProfile(updates);

      if (success) {
        const updatedUser = {
          ...user,
          name,
          email,
          defaultModelApiName: selectedModel?.apiName, // Update local state with the apiName
        };
        setUser(updatedUser as UserProfile);
        toast({
          title: 'All Changes Saved',
          description: 'Your profile and settings have been updated successfully.',
        });
      }
    } catch (error) {
      console.error('An unexpected error occurred during save:', error);
      toast({
        title: 'Error Saving Changes',
        description: `An unexpected error occurred: ${(error as Error).message}`,
        variant: 'destructive',
      });
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 2MB',
        variant: 'destructive',
      });
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }
    try {
      setUploading(true);
      const result = await uploadAvatar(file);
      if (result.success && result.publicUrl) {
        if (user) {
          setUser({ ...user, avatar: result.publicUrl } as UserProfile);
        }
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteAvatar = async () => {
    try {
      const result = await deleteAvatar();
      if (result.success && user) {
        setUser({ ...user, avatar: undefined } as UserProfile);
      }
    } catch (error) {
      console.error('Error deleting avatar:', error);
    }
  };

  const avatarUrl = profile?.avatar_url || user?.avatar;

  let isFounder = false;
  let createdAt: string | undefined = undefined;
  if (profile?.created_at) createdAt = profile.created_at;
  else if (user && (user as any).created_at) createdAt = (user as any).created_at;

  if (createdAt) {
    try {
      const createdDate = new Date(createdAt);
      isFounder = createdDate < FOUNDERS_END_DATE;
    } catch {
      isFounder = false;
    }
  }

  const userBadges = isFounder ? [{ type: 'founder' as const }] : [];

  if (profileLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">User Profile</h1>
        <Link to="/">
          <Button variant="outline" className="gap-2">
            <Home size={18} />
            Back to Home
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <User size={20} />
              <CardTitle>Profile Information</CardTitle>
            </div>
            <CardDescription>Update your personal information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center mb-6 space-y-4">
              <div className="relative group flex flex-col items-center">
                <Avatar
                  className="h-24 w-24 border-2 border-primary/10 group-hover:border-primary/50 transition-all cursor-pointer"
                  onClick={handleAvatarClick}
                >
                {
                  avatarUrl ? (
                    <AvatarImage src={avatarUrl} alt={name} />
                  ) : (
                    <AvatarFallback>
                      <Bot className="h-12 w-12 text-muted-foreground" />
                    </AvatarFallback>
                  )
                }
                </Avatar>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={handleAvatarClick} className="flex items-center gap-1">
                    <Upload className="h-4 w-4" />
                    Upload
                  </Button>
                  {avatarUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteAvatar}
                      className="flex items-center gap-1 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
                <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleAvatarUpload} />
              </div>

              {userBadges.length > 0 && (
                <div className="flex flex-col items-center w-full">
                  <div className="text-sm font-medium text-primary mb-1">Badges</div>
                  <div className={`grid gap-2 ${userBadges.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {userBadges.map((badge, idx) => (
                      <UserBadge key={badge.type + idx} type={badge.type} />
                    ))}
                  </div>
                </div>
              )}

              {uploading && <p className="text-sm text-primary animate-pulse mt-2">Uploading...</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name" disabled={profileLoading} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" value={email} disabled placeholder="Email Address" />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
          </CardContent>
        </Card>

        <PasswordSettings />
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSaveAll} className="w-full md:w-auto" disabled={uploading || profileLoading}>
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default Profile;